// react
import { MouseEvent, ReactNode } from 'react';

// types
import { ActionButton } from '../../../@types';

// i18n
import { useTranslation } from 'react-i18next';

// mui
import {
	Button,
	Dialog,
	DialogProps,
	IconButton,
	Grid,
	Typography,
	TypographyProps,
	useMediaQuery,
} from '@mui/material';
import { LoadingButton } from '@mui/lab';

// ui
import { IconCircle, IconCircleProps } from '../../ui/IconCircle/IconCircle';

// icons
import { ClearOutlined as IconClear } from '@mui/icons-material';

// theme
import { theme } from '../../../theme';
import {
	IconCircleTransaction,
	IconCircleTransactionProps,
} from '../../ui/IconCircleTransaction/IconCircleTransaction';

// props
export type DialogCloseEvent = MouseEvent<HTMLButtonElement | HTMLAnchorElement>;

export interface DialogActionsProps extends Omit<
	DialogProps,
	'content' | 'maxWidth' | 'onClose' | 'open' | 'title'
> {
	actions?: ActionButton[];
	alignItems?: CSSStyleDeclaration['alignItems'];
	children?: ReactNode;
	content?: ReactNode;
	hasClose?: boolean;
	IconCircleProps?: IconCircleProps;
	IconCircleTransactionProps?: IconCircleTransactionProps;
	isOpen: boolean;
	maxWidth?: number;
	onClose: (e: DialogCloseEvent, reason?: 'escapeKeyDown' | 'backdropClick' | 'btnClick') => void;
	subtitle?: TypographyProps;
	title?: TypographyProps;
}

export const DialogActions = ({
	actions = [],
	alignItems = 'flex-start',
	children,
	content,
	hasClose = false,
	IconCircleProps,
	IconCircleTransactionProps,
	isOpen = false,
	maxWidth = 500,
	onClose,
	subtitle,
	title,
	...props
}: DialogActionsProps) => {
	// hooks
	const { t } = useTranslation();

	// vars
	const isFullScreen = useMediaQuery(theme.breakpoints.down('lg'));

	const handleClose: DialogProps['onClose'] = (e, reason) => {
		// prevent closing if backdrop is clicked
		if (reason === 'backdropClick') {
			return;
		}

		if (onClose) {
			onClose(e as DialogCloseEvent, reason);
		}
	};

	const handleCloseButton = (e: MouseEvent<HTMLButtonElement>) => {
		if (onClose) {
			onClose(e, 'btnClick');
		}
	};

	const renderTitle = ({ id, title }: { id: string; title: TypographyProps }) => {
		if (!title) {
			return false;
		}

		const { children, fontWeight, variant, ...titleProps } = title;

		return (
			<Typography fontWeight={fontWeight || 600} id={id} variant={variant || 'h2'} {...titleProps}>
				{children}
			</Typography>
		);
	};

	let iconCircleContent: ReactNode = null;

	if (IconCircleProps) {
		iconCircleContent = <IconCircle size="lg" {...IconCircleProps} />;
	} else if (IconCircleTransactionProps) {
		iconCircleContent = <IconCircleTransaction size="lg" {...IconCircleTransactionProps} />;
	}

	return (
		<Dialog
			{...props}
			open={isOpen}
			onClose={handleClose}
			aria-labelledby="dialog-actions-title"
			aria-describedby="dialog-actions-description"
			fullScreen={isFullScreen}
			PaperProps={{
				sx: (theme) => {
					return {
						padding: {
							xs: theme.spacing(2),
							md: theme.spacing(4),
						},
						maxWidth: isFullScreen ? '100%' : maxWidth,
					};
				},
			}}>
			<Grid container spacing={2}>
				{(iconCircleContent || title) && (
					<Grid item xs={12}>
						<Grid container direction="column" alignItems={alignItems} spacing={1}>
							{iconCircleContent && <Grid item>{iconCircleContent}</Grid>}
							{(title || subtitle) && (
								<Grid item sx={{ textAlign: alignItems === 'center' ? 'center' : 'left' }}>
									{title && renderTitle({ id: 'dialog-actions-title', title })}
									{subtitle &&
										renderTitle({
											id: 'dialog-actions-subtitle',
											title: {
												color: 'brand.grey.500',
												fontWeight: 500,
												variant: 'h4',
												...subtitle,
											},
										})}
								</Grid>
							)}
						</Grid>
					</Grid>
				)}

				<Grid id="dialog-actions-description" item xs={12}>
					{children}
					{typeof content === 'string' ? <Typography>{content}</Typography> : content}
				</Grid>

				{actions.length > 0 && (
					<Grid item xs={12}>
						<Grid alignItems="center" container justifyContent="flex-end" spacing={2}>
							{actions.map((action, i) => {
								const { children, ...propsAction } = action;
								const Component = 'loading' in action ? LoadingButton : Button;
								return (
									<Grid item key={`dialog-action-btn-${i}`}>
										<Component {...propsAction}>{children}</Component>
									</Grid>
								);
							})}
						</Grid>
					</Grid>
				)}
			</Grid>
			{hasClose && (
				<IconButton
					aria-label={t('close')}
					onClick={handleCloseButton}
					size="small"
					sx={(theme) => {
						return {
							position: 'absolute',
							right: theme.spacing(1),
							top: theme.spacing(1),
						};
					}}>
					<IconClear />
				</IconButton>
			)}
		</Dialog>
	);
};
